import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { keys } from 'lodash';
import { LOOKUP } from '../models';
import { TranslateService } from '@ngx-translate/core';
import { CrudBaseService } from '../crud.service';
import { PlatformLookup } from '@shared/models/platform-lookup';
import { TenantName } from '@shared/models/tenant-name';
import { EntityName } from '@shared/models/entity-name';
import { HttpHelper } from 'eightcap-core/helpers';

export class LookupModel {
    constructor(public code: string, public name: string) {}
}

export class CountryCurrencyLookupModel extends LookupModel {
    constructor(countryCode: string, countryName: string, public baseCurrency: string, public asic: string) {
        super(countryCode, countryName);
    }
}

export class CountryLookupModel extends LookupModel {
    translationKey: string;
    constructor(
        countryCode: string,
        countryName: string,
        public entities: EntityName[],
        public defaultEntity: EntityName
    ) {
        super(countryCode, countryName);
    }
}

export class TaxIdentificationModel {
    constructor(countryName: string, TIN: string) {}
}

export enum RiskLevel {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High',
    Restricted = 'Restricted'
}

export class CountryRiskMatrix {
    constructor(public countryCode: string, public riskLevel: RiskLevel) {}
}

export interface EntityConfig {
    default_jurisdiction: { [key: string]: Array<string> };
    jurisdiction: { [key: string]: Array<string> };
}

@Injectable({
    providedIn: 'root'
})
export class LookupService extends CrudBaseService<any> {
    private _countries$: Observable<CountryLookupModel[]>;
    private _annualIncomes$: Observable<LookupModel[]>;
    private _sourceOfFunds$: Observable<LookupModel[]>;
    private _industries$: Observable<LookupModel[]>;
    private _leverages$: Observable<LookupModel[]>;
    private _riskLevels$: Observable<LookupModel[]>;
    private _accountType$: Observable<LookupModel[]>;
    private _promotionTypes$: Observable<LookupModel[]>;

    _taxIdentification$: Observable<{}[]>;

    _lookupValues$: Observable<any>;

    constructor(private translateService: TranslateService, httpClient: HttpClient) {
        super('/lookup', httpClient);
        this._lookupValues$ = this.httpClient.get('assets/lookup/lookup_values.json').pipe(shareReplay());
    }

    lookup(lookupType: string): Observable<LookupModel[]> {
        return this.httpClient.get(this.basePath + `/${lookupType}`).pipe(map((res: any) => res && res.data));
    }

    get localeLookup$(): Observable<LookupModel[]>{
        return this.lookup(LOOKUP.LOCALE).pipe(
            map((locales: LookupModel[]) => {
                return [...new Map(locales.map((v) => [v.name, v])).values()];
            })
        );

    }
    get titles$(): Observable<LookupModel[]> {
        return this.load('TITLE').pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }
    //TODO obsolete
    get platformOfASIC$(): Observable<LookupModel[]> {
        return of([PlatformLookup.MT4L, PlatformLookup.MT5L]).pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }
    //TODO obsolete
    get platformDemo$(): Observable<LookupModel[]> {
        return of([PlatformLookup.MT4D, PlatformLookup.MT5D]).pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }

    get tenants$(): Observable<TenantName[]> {
        return of(
            environment.entityOptions.map((tenants) => {
                return tenants.code;
            })
        );
    }

    get platforms$(): Observable<string[]> {
        return of(environment.platformLookups);
    }

    get currency$(): Observable<LookupModel[]> {
        return this.load('CURRENCY').pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }

    get currenciesOfASIC$(): Observable<LookupModel[]> {
        return this.load('ASIC_CURRENCY').pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }

    get depositCurrencies$(): Observable<LookupModel[]> {
        return this.load('DEPOSIT_CURRENCY').pipe(
            map((rawValues: any) => {
                return rawValues.map((key: string) => new LookupModel(key, key));
            })
        );
    }

    get applicationTypes$(): Observable<LookupModel[]> {
        return this.load('APPLICATION_TYPE').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get accountTypes$(): Observable<LookupModel[]> {
        return this.load('ACCOUNT_TYPE').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get timeAtAddresses$(): Observable<LookupModel[]> {
        return this.load('TIME_AT_ADDRESS').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get employmentStatuses$(): Observable<LookupModel[]> {
        return this.load('EMPLOYMENT_STATUS').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get annualIncomes$(): Observable<LookupModel[]> {
        if (!this._annualIncomes$) {
            this._annualIncomes$ = this.lookup(LOOKUP.ANNUAL_INCOME).pipe(shareReplay());
        }

        return this._annualIncomes$;
    }

    get sourceOfFunds$(): Observable<LookupModel[]> {
        if (!this._sourceOfFunds$) {
            this._sourceOfFunds$ = this.lookup(LOOKUP.SOURCE_OF_DEALING_FUNDS).pipe(shareReplay());
        }

        return this._sourceOfFunds$;
    }

    get liquidNetWorths$(): Observable<LookupModel[]> {
        return this.load('LIQUID_NET_WORTH').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get investmentObjectives$(): Observable<LookupModel[]> {
        return this.load('INVESTMENT_OBJECTIVE').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get initialDeposits$(): Observable<LookupModel[]> {
        return this.load('INITIAL_DEPOSIT').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }

    get employmentIndustries$(): Observable<LookupModel[]> {
        if (!this._industries$) {
            this._industries$ = this.lookup(LOOKUP.INDUSTRY).pipe(shareReplay());
        }

        return this._industries$;
    }
    get q1Answers$(): Observable<LookupModel[]> {
        return this.load('Q1_ANSWERS').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }
    get question1$(): Observable<LookupModel[]> {
        return this.load('QUESTION1').pipe(
            map((rawValues: any) => {
                return keys(rawValues).map((key: string) => new LookupModel(key, rawValues[key]));
            })
        );
    }
    get yesNo$(): Observable<LookupModel[]> {
        return this.load('YES_NO').pipe(
            map((rawValues: any) => {
                return keys(rawValues)
                    .map((key: string) => new LookupModel(key, rawValues[key]))
                    .reverse();
            })
        );
    }
    get leverageAsic$(): Observable<LookupModel[]> {
        return this.load('LEVERAGE').pipe(
            map((rawValues: any) => {
                return keys(rawValues)
                    .map((key: string) => new LookupModel(key, rawValues[key]))
                    .reverse();
            })
        );
    }

    get countries$(): Observable<CountryLookupModel[]> {
        if (!this._countries$) {
            this._countries$ = this.httpClient
                .get('assets/lookup/country_licence_jurisdiction_data.csv', {
                    responseType: 'text'
                })
                .pipe(
                    map((data) => {
                        const results = [];
                        const countries = data.split('\n');
                        countries.shift();
                        countries.forEach((country) => {
                            if (country) {
                                const value = country.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
                                const entities: EntityName[] = [];
                                if (value[2].toLowerCase() === 'true') {
                                    entities.push(EntityName.ASIC);
                                }

                                if (value[3].toLowerCase() === 'true') {
                                    entities.push(EntityName.VFSC);
                                }

                                if (value[4].toLowerCase() === 'true') {
                                    entities.push(EntityName.SCB);
                                }

                                if (value[5].toLowerCase() === 'true') {
                                    entities.push(EntityName.FCA);
                                }

                                if (value[6].toLowerCase() === 'true') {
                                    entities.push(EntityName.CYSEC);
                                }

                                results.push({
                                    code: value[0],
                                    name: value[1].replace(/["]+/g, ''),
                                    translationKey: `common.country.${value[0].toLowerCase()}`,
                                    entities,
                                    defaultEntity: value[7]
                                } as CountryLookupModel);
                            }
                        });

                        return results.sort((countryA, countryB) => {
                            const countryNameA = countryA.name.toUpperCase();
                            const countryNameB = countryB.name.toUpperCase();
                            return countryNameA.localeCompare(countryNameB);
                        });
                    }),
                    shareReplay()
                );
        }

        return this._countries$;
    }

    get taxIdentification$(): Observable<{}[]> {
        if (!this._taxIdentification$) {
            this._taxIdentification$ = this.httpClient.get('assets/lookup/TIN.csv', { responseType: 'text' }).pipe(
                map((data) => {
                    const results = [];
                    const taxIndentifications = data.split('\n');
                    taxIndentifications.forEach((taxIdentification) => {
                        if (taxIdentification) {
                            const value = taxIdentification.split(',');
                            if (value[0] && value[1] && value[2]) {
                                results.push({
                                    countryCode: value[0],
                                    countryName: value[1],
                                    countryTIN: value[2]
                                });
                            }
                        }
                    });

                    return results;
                })
            );
        }

        return this._taxIdentification$;
    }

    get availableCountries$(): Observable<CountryLookupModel[]> {
        return this.countries$;
    }

    get countryGroups$(): Observable<any[]> {
        const mostPopularCountries = ['cn', 'fr', 'de', 'it', 'kr', 'es', 'th'].map((value: string) =>
            value.toUpperCase()
        ); // Take these to config.

        const mostPopularCountries$ = this.availableCountries$.pipe(
            map((values: LookupModel[]) =>
                values.filter((value: LookupModel) => mostPopularCountries.indexOf(value.code) !== -1)
            )
        );

        const otherCountries$ = this.availableCountries$.pipe(
            map((values: LookupModel[]) =>
                values.filter((value: LookupModel) => mostPopularCountries.indexOf(value.code) === -1)
            )
        );

        return mostPopularCountries$.pipe(
            map((co: LookupModel[]) => {
                return [
                    {
                        description: 'Most Popular',
                        values: co
                    }
                ];
            }),
            switchMap((mostPopular: any[]) =>
                otherCountries$.pipe(
                    map((co: LookupModel[]) => {
                        mostPopular.push({
                            description: 'Others',
                            values: co
                        });
                        return mostPopular;
                    })
                )
            )
        );
    }

    get genders$(): Observable<LookupModel[]> {
        return of<LookupModel[]>([
            {
                code: 'F',
                name: 'common.gender.f'
            },
            {
                code: 'M',
                name: 'common.gender.m'
            },
            {
                code: 'O',
                name: 'common.gender.o'
            }
        ]);
    }

    get entityConfig$(): Observable<EntityConfig> {
        return this.httpClient.get<EntityConfig>('assets/lookup/lookup-entity-config.json');
    }

    stageLookup(): Observable<LookupModel[]> {
        return this.httpClient.get(`${this.basePath}/lookup_states_provinces`).pipe(map((res: any) => res && res.data));
    }

    get leverages$(): Observable<LookupModel[]> {
        if (!this._leverages$) {
            this._leverages$ = this.lookup(LOOKUP.LEVERAGE).pipe(shareReplay());
        }

        return this._leverages$;
    }

    get accountTypeLookup$(): Observable<LookupModel[]> {
        if (!this._accountType$) {
            this._accountType$ = this.lookup(LOOKUP.ACCOUNT_TYPE).pipe(
                map((values: LookupModel[]) => {
                    values.unshift(new LookupModel(null, ''));
                    return values;
                }),
                shareReplay()
            );
        }

        return this._accountType$;
    }

    get riskLevels$(): Observable<LookupModel[]> {
        if (!this._riskLevels$) {
            this._riskLevels$ = this.lookup(LOOKUP.RISK_LEVEL).pipe(
                map((values: LookupModel[]) => {
                    values.unshift(new LookupModel(null, ''));
                    return values;
                }),
                shareReplay()
            );
        }

        return this._riskLevels$;
    }

    get promotionTypes$(): Observable<LookupModel[]> {
        if (!this._promotionTypes$) {
            this._promotionTypes$ = this.lookup(LOOKUP.PROM0TION_TYPE).pipe(shareReplay());
        }

        return this._promotionTypes$;
    }

    private load(key: string): Observable<any> {
        return this._lookupValues$.pipe(map((value: any) => value[key]));
    }

    insertLookupData(lookupData: LookupModel, lookupType: string): Observable<LookupModel[]> {
        return this.httpClient.post<LookupModel[]>(`${this.basePath}/${lookupType}`, {
            data: lookupData
        });
    }

    updateLookupData(lookupData: LookupModel, lookupType: string): Observable<LookupModel[]> {
        return this.httpClient.patch<LookupModel[]>(`${this.basePath}/${lookupType}`, {
            data: lookupData
        });
    }

    deleteLookupData(lookupType: string, lookupCode: string): Observable<boolean> {
        return this.httpClient.delete<boolean>(`${this.basePath}/${lookupType}/${lookupCode}`);
    }

    getLookupWithPagination(lookupType: string, filterParams): Observable<LookupModel[]> {
        return this.httpClient
            .get(this.basePath + `/${lookupType}/pagination`, {
                params: HttpHelper.objectToHttpParams({ ...filterParams })
            })
            .pipe(map((res: any) => res && res.data));
    }
}
